import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { 
  ArrowRightIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  HeartIcon,
  MicrophoneIcon,
  ClipboardDocumentCheckIcon,
  ShareIcon,
  ClockIcon,
  EyeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { ImageOptimizer } from '@/components/seo/ImageOptimizer';
import careToolsHero from '@/assets/thrivefamilyplus-hero.png';
import avatarWoman1 from '@/assets/avatar-woman-1.jpg';
import avatarMan1 from '@/assets/avatar-man-1.jpg';
import avatarWoman2 from '@/assets/avatar-woman-2.jpg';
import CareToolsComparison from '@/components/care-tools/CareToolsComparison';


const CareTools = () => {
  const { openModal } = useEarlyAdopter();

  const handleGetStarted = () => {
    openModal('ThriveFamily - Get Started');
  };

  const featureBlocks = [
    {
      title: "From 'I Think' to 'I Know'",
      description: "Stop guessing if medications were taken or if symptoms are new. ThriveFamily+ gives you tools to log the details that matter.",
      underTheHood: [
        { name: "Medication & Vitals Tracking", description: "Simple logs for meds, blood pressure, or weight.", icon: ClipboardDocumentCheckIcon },
        { name: "Symptom Timeline", description: "See exactly when a cough or confusion started.", icon: ClockIcon }
      ],
      calloutText: "Know Clearly"
    },
    {
      title: "Turn Observations into a Record",
      description: "You notice things others don't. A shuffle in the walk. A forgotten word. Capture these moments when they happen so they don't get lost in the shuffle.",
      underTheHood: [
        { name: "Quick Capture", description: "Voice-to-text notes for when your hands are full.", icon: MicrophoneIcon },
        { name: "Pattern Recognition", description: "See trends over weeks, not just days.", icon: ChartBarIcon }
      ],
      calloutText: "Capture Easily"
    },
    {
      title: "Walk Into Appointments Prepared",
      description: "Doctors love data. Instead of vague descriptions ('She's been tired'), show them the record. It changes the conversation from anecdotal to clinical.",
      underTheHood: [
        { name: "Care Reports", description: "Easy-to-read summaries you can show at appointments.", icon: DocumentTextIcon },
        { name: "Provider Context", description: "Keeps all doctor info and instructions in one spot.", icon: UserGroupIcon }
      ],
      calloutText: "Be Prepared"
    },
    {
      title: "Organized Care, Shared Load",
      description: "Just because you track it doesn't mean you have to hide it. The rest of the family can see what you choose to share, so they understand the reality of the situation.",
      underTheHood: [
        { name: "Selective Sharing", description: "You decide which health details the rest of the family sees.", icon: EyeIcon },
        { name: "Task Assignment", description: "Need a sibling to handle a prescription pickup? Assign it directly from the record.", icon: ShareIcon }
      ],
      calloutText: "Share Clearly"
    }
  ];


  const thriveFamilyFit = [
    "Your parents are mostly independent",
    "You mainly need to know they are safe and coordinate visits",
    "The goal is communication"
  ];

  const thriveFamilyPlusFit = [
    "You are managing medications or chronic conditions",
    "You are noticing physical or cognitive decline",
    "You are the one answering the doctor's questions",
    "The goal is management"
  ];

  return (
    <>
      <EnhancedSEO 
        title="ThriveFamily+ - Care Tools for the Family Member Who Does the Heavy Lifting | SeniorThrive"
        description="ThriveFamily+ adds management tools for the person doing the day-to-day care. Turn your mental load into a clear, reliable record."
        keywords="ThriveFamily+, family care tools, care coordination, senior care, family caregiver tools, care documentation"
        canonical="https://seniorthrive.com/family-caregiver-tools"
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" },
          { name: "ThriveFamily+", url: "https://seniorthrive.com/family-caregiver-tools" }
        ]}
        schema={{
          type: "Service",
          name: "ThriveFamily+",
          description: "Advanced family care tools for the frontline family member",
          provider: "SeniorThrive"
        }}
      />
      
      {/* HERO SECTION */}
      <div className="container mx-auto px-4 py-20">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary-glow/10 rounded-2xl"></div>
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary/15 to-transparent rounded-full blur-2xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center min-h-[600px] p-8 lg:p-16">
            {/* Content Side */}
            <div className="space-y-8 order-2 lg:order-1">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 bg-primary/10 backdrop-blur-sm px-4 py-2 rounded-full border border-primary/30">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider">ThriveFamily+</span>
                </div>
              </div>
              
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-5xl font-bold leading-hero">
                  Care Tools for the Family Member Who Does the{' '}
                  <span className="text-primary">Heavy Lifting</span>
                </h1>
                <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                  ThriveFamily handles family coordination. ThriveFamily+ adds management tools for the person doing the day-to-day care.
                </p>
              </div>
              
              {/* Social proof */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex -space-x-2">
                  <img src={avatarWoman1} alt="ThriveFamily+ user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                  <img src={avatarMan1} alt="ThriveFamily+ user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                  <img src={avatarWoman2} alt="ThriveFamily+ user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                </div>
                <span>Join primary caregivers finding clarity</span>
              </div>
              
              {/* CTA Section */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" variant="default" onClick={handleGetStarted}>
                    Start with ThriveFamily
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  ThriveFamily+ is available as an add-on inside the app.
                </p>
              </div>
            </div>
            
            {/* Image Side */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-primary-glow/20 rounded-2xl transform rotate-2"></div>
                <div className="relative">
                  <ImageOptimizer 
                    src={careToolsHero} 
                    alt="Family member coordinating care with ThriveFamily+ tools" 
                    imgClassName="w-full h-[400px] lg:h-[500px] object-cover rounded-2xl shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-500"
                    sizes="(min-width: 1024px) 600px, 100vw"
                    width={600}
                    height={500}
                    quality={85}
                    priority={true}
                  />
                  <div className="absolute -bottom-4 -right-4 bg-background/95 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-primary/20">
                    <span className="text-sm font-medium text-primary">✓ Organized & Clear</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-20 px-4">
        <div className="container mx-auto">

          {/* SECTION 1: THE PROBLEM - Why This Exists */}
          <div className="py-16 mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                    <HeartIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-foreground">You're Not Just "Checking In"</h2>
                </div>
                
                <div className="space-y-4">
                  <p className="text-lg text-foreground leading-relaxed font-medium">
                    You're the one who notices when something's off. You're the one the doctor looks to for answers. You're carrying more than anyone realizes.
                  </p>
                  <p className="text-lg text-muted-foreground">And right now, most of that critical information lives in your head.</p>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    "When did that symptom start?",
                    "Did she take her meds today?",
                    "I know something's wrong—but I can't prove it."
                  ].map((question, i) => (
                    <div key={i} className="flex items-center gap-3 bg-muted/50 p-4 rounded-xl border border-border/50">
                      <span className="text-2xl text-amber-500">?</span>
                      <span className="text-foreground italic text-sm">{question}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-primary/5 p-6 rounded-xl border-l-4 border-primary">
                  <p className="text-foreground font-medium text-lg">
                    ThriveFamily+ turns your mental load into a clear, reliable record.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: THE SOLUTION - What ThriveFamily+ Is */}
          <div className="py-16 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">What ThriveFamily+ Is</h2>
              <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                ThriveFamily+ is the management layer built for the person coordinating day-to-day care. It captures the details so you don't have to carry them.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { text: "It captures details.", sub: "So you don't have to memorize them.", icon: DocumentTextIcon },
                { text: "It tracks patterns.", sub: "So you catch issues early.", icon: ChartBarIcon },
                { text: "It builds credibility.", sub: "So doctors take your observations seriously.", icon: ShieldCheckIcon }
              ].map((item, i) => (
                <div key={i} className="bg-background/50 backdrop-blur-sm p-6 rounded-xl border border-primary/10 text-center hover:border-primary/30 transition-colors">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-foreground font-medium mb-1">{item.text}</p>
                  <p className="text-sm text-muted-foreground">{item.sub}</p>
                </div>
              ))}
            </div>
            
            <p className="text-center text-muted-foreground mt-8">
              ThriveFamily+ is added from inside ThriveFamily when your role shifts from coordination to management.
            </p>
          </div>

          {/* SECTION 3: THE TRANSFORMATION - Before / After */}
          <CareToolsComparison />

          {/* SECTION 4: HOW IT HELPS - The Features */}
          <div className="py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">How ThriveFamily+ Helps</h2>
                <p className="text-xl text-muted-foreground">Tools that turn your mental load into clarity.</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {featureBlocks.map((block, idx) => (
                  <div key={idx} className="bg-background/50 backdrop-blur-sm p-8 rounded-2xl border border-primary/10 hover:border-primary/30 hover:shadow-lg transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        {idx === 0 && <ClipboardDocumentCheckIcon className="w-6 h-6 text-primary" />}
                        {idx === 1 && <MicrophoneIcon className="w-6 h-6 text-primary" />}
                        {idx === 2 && <DocumentTextIcon className="w-6 h-6 text-primary" />}
                        {idx === 3 && <ShareIcon className="w-6 h-6 text-primary" />}
                      </div>
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider bg-primary/10 px-3 py-1 rounded-full">
                        {block.calloutText}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-foreground mb-3">{block.title}</h3>
                    <p className="text-muted-foreground mb-6">{block.description}</p>
                    
                    <div className="space-y-3">
                      {block.underTheHood.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                          <feature.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium text-foreground text-sm">{feature.name}</span>
                            <p className="text-xs text-muted-foreground">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 5: THE FULL TOOLKIT */}
          <div className="py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">The Full Toolkit</h2>
                <p className="text-xl text-muted-foreground">Everything ThriveFamily+ adds to help you manage care.</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { 
                    icon: ClipboardDocumentCheckIcon, 
                    title: "Condition-Specific Care Packs", 
                    description: "Toggle on dementia, diabetes, heart failure, or any of 13 conditions—get tailored checklists and observation prompts." 
                  },
                  { 
                    icon: ClockIcon, 
                    title: "Visit Scheduling & Check-In/Check-Out", 
                    description: "Track when care happens—and when it doesn't." 
                  },
                  { 
                    icon: ExclamationCircleIcon, 
                    title: "Incident & Fall Reporting", 
                    description: "Document what happened, when, and what you did about it." 
                  },
                  { 
                    icon: MicrophoneIcon, 
                    title: "Voice Notes → Actions", 
                    description: "Say it out loud. It becomes a task, a reminder, or a note." 
                  },
                  { 
                    icon: ShareIcon, 
                    title: "Care Handoffs", 
                    description: "When someone else takes over, nothing gets lost." 
                  },
                  { 
                    icon: DocumentTextIcon, 
                    title: "Supply Requests", 
                    description: "Track what's running low before it runs out." 
                  },
                  { 
                    icon: ChartBarIcon, 
                    title: "Care Load Tracking", 
                    description: "Finally see how much you're actually doing." 
                  },
                  { 
                    icon: HeartIcon, 
                    title: "Self-Care Reminders", 
                    description: "You can't pour from an empty cup. We'll remind you to refill." 
                  }
                ].map((feature, i) => (
                  <div key={i} className="bg-background/50 backdrop-blur-sm p-6 rounded-xl border border-primary/10 hover:border-primary/30 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 6: THE DISTINCTION - Is This For You? */}
          <div className="py-20">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Is This For You?
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* ThriveFamily Standard */}
                <div className="relative py-8 px-8 rounded-2xl bg-background/50 backdrop-blur-sm border border-border/50 hover:border-primary/20 transition-all">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-muted rounded-xl">
                      <UserGroupIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">Stick with ThriveFamily if:</h3>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-6">
                    {thriveFamilyFit.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/30">
                        <CheckCircleIcon className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    asChild
                    variant="outline"
                    size="lg" 
                    className="w-full"
                  >
                    <Link to="/thrivefamily">Learn About ThriveFamily</Link>
                  </Button>
                </div>
                
                {/* ThriveFamily+ */}
                <div className="relative py-8 px-8 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30">
                  <div className="absolute -top-3 right-6">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      Add-On
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary/20 rounded-xl">
                      <ShieldCheckIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">Add ThriveFamily+ if:</h3>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-6">
                    {thriveFamilyPlusFit.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-primary/20">
                        <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    size="lg" 
                    className="w-full"
                    onClick={handleGetStarted}
                  >
                    Get Started with ThriveFamily
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* FINAL CTA */}
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-glow to-primary/80"></div>
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary-foreground/20 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary-foreground/15 to-transparent rounded-full blur-2xl"></div>
            </div>
            
            <div className="relative text-center p-12 lg:p-16 text-primary-foreground">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                You're Already Doing the Work.
              </h2>
              <h3 className="text-2xl lg:text-3xl font-bold mb-6 opacity-90">
                ThriveFamily+ Helps It Count.
              </h3>
              <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto">
                Stop carrying the details in your head. Get the tools that make caregiving manageable.
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-primary-foreground/90">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">Seamless Upgrade</span>
                </div>
                <div className="flex items-center gap-2">
                  <HeartIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">Keep All Your Data</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  variant="secondary"
                  className="text-lg px-8 py-4 bg-white text-primary hover:bg-white/95 shadow-lg hover:shadow-xl transition-all group font-semibold"
                  onClick={handleGetStarted}
                >
                  Get Started with ThriveFamily
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              
              <p className="mt-6 text-sm opacity-80">
                Add ThriveFamily+ anytime inside the app.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CareTools;
